/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import { minimalTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { observes } from '@ember-decorators/object';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class FieldsController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  DataType = DataType;
  InputType = InputType;
  numericTypes = Object.freeze([
    { value: 3, label: 'Integer (whole numbers)' },
    { value: 4, label: 'Float (decimal numbers)' },
  ]);
  selectDataTypes = Object.freeze([
    { value: 1, label: 'String' },
    { value: 3, label: 'Integer (whole numbers)' },
  ]);
  editorOptions = Object.freeze(minimalTinyMCEConfig());

  @tracked
  field = null;

  @tracked
  fieldId = '';

  @tracked
  fieldLabel = '';

  fieldKey = '';

  @computed('model.sets.@each.id', 'field.optionSetsArray.[]')
  get remainingFieldSets() {
    return this.get('model.sets').filter((source) => {
      return !this.get('field.optionSetsArray').some(function (set) {
        return source.id === set.id;
      });
    });
  }

  @tracked
  fieldType = null;

  @action
  setFieldType(value) {
    this.fieldType = value;
  }

  @tracked
  numericType = null;

  @action
  setNumericType(value) {
    this.numericType = value;
  }

  @tracked
  selectDataType = null;

  @action
  setSelectDataType(value) {
    this.selectDataType = value;
  }

  @observes('fieldType.value', 'selectDataType')
  _updateFieldType() {
    let type = this.get('fieldType.value');
    if (!isEmpty(type) && !isEmpty(this.field)) {
      this.field.set('inputType', type);
      if (type !== InputType.NUMBER) {
        let dataType = 0;
        switch (type) {
          case InputType.CHECKBOX:
          case InputType.MULTI_SELECT:
            dataType = DataType.LIST;
            break;
          case InputType.SWITCH:
            dataType = DataType.BOOLEAN;
            break;
          case InputType.SELECT:
            dataType = this.get('selectDataType.value') || DataType.STRING;
            break;
          default:
            dataType = DataType.STRING;
            break;
        }
        this.field.set('dataType', dataType);
      }
    }
    if (type === InputType.NUMBER) {
      if (!isEmpty(this.field)) {
        switch (this.field.dataType) {
          case DataType.INTEGER:
          case DataType.FLOAT:
            this.numericType = this.getNumericType(this.field.dataType);
            break;
          default:
            this.numericType = this.getNumericType(DataType.INTEGER);
            break;
        }
      } else {
        this.numericType = this.getNumericType(DataType.INTEGER);
      }
    }
  }

  @observes('fieldType.value', 'numericType.value')
  _updateNumericType() {
    if (this.get('fieldType.value') === InputType.NUMBER) {
      let type = this.get('numericType.value');
      if (!isEmpty(type) && !isEmpty(this.field)) {
        this.field.set('dataType', type);
      }
    }
  }

  @computed('fieldType.value')
  get fieldTypeHasOptions() {
    let fieldType = this.get('fieldType.value');
    switch (fieldType) {
      case InputType.RADIO:
      case InputType.CHECKBOX:
      case InputType.SELECT:
      case InputType.MULTI_SELECT:
      case InputType.SORTABLE_LIST:
        return true;
    }

    return false;
  }

  @computed('fieldType.value')
  get fieldTypeHasStaticContent() {
    let fieldType = this.get('fieldType.value');
    switch (fieldType) {
      case InputType.TITLE:
      case InputType.DESCRIPTION:
        return true;
    }

    return false;
  }

  @tracked
  newOption = null;
  isSortable = false;

  @computed('model.fields.[]')
  get sortedFields() {
    return this.model.fields
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @tracked
  fieldDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked
  deleteFieldDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked
  errorMessage = '';

  // toggleDialog opens the field dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('fieldDialogOpen');
    if (!this.fieldDialogOpen) {
      // rollback edited optionSetIds as well.
      rollbackRecord(this.field);
      this.resetForm();
    }
  }

  @action
  toggleFieldDialog() {
    this.toggleDialog();
  }

  @action
  editField(id) {
    this.store.findRecord('school-options/field', id).then((field) => {
      let props = {
        field,
      };
      let inputType = field.get('inputType');
      props.fieldType = this.getInputType(inputType);

      switch (inputType) {
        case InputType.NUMBER:
          props.numericType = this.getNumericType(field.get('dataType'));
          break;

        case InputType.SELECT:
          props.selectDataType = findBy(
            this.selectDataTypes,
            'value',
            field.get('dataType'),
          );
          break;

        default:
          // Reset selectDataType to STRING to prevent issues when going from
          // a numeric select to a non-select with options (i.e. radio
          // buttons or checkboxes), which can (currently) only by stringy.
          props.selectDataType = findBy(
            this.selectDataTypes,
            'value',
            DataType.STRING,
          );
          break;
      }

      this.setProperties(props);
    });
    this.fieldId = id;
    this.toggleDialog();
  }

  @action
  saveField() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let action = 'create';

    if (isEmpty(this.fieldId)) {
      this.field.set('headingId', this.get('model.heading'));
    } else {
      action = 'update';
    }

    switch (this.get('fieldType.value')) {
      case InputType.NUMBER:
        this.field.set('dataType', this.get('numericType.value'));
        break;
      case InputType.SELECT:
        this.field.set('dataType', this.get('selectDataType.value'));
        break;
    }

    return this.field
      .save()
      .then(() => {
        this.fieldDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, action);
      })
      .then(() => {
        this.saveInProgress = false;
        if (action === 'create') {
          this.router.refresh('school-options.fields');
        }
      });
  }

  @action
  deleteField(id) {
    this.errorMessage = '';
    this.store.findRecord('school-options/field', id).then((field) => {
      this.fieldLabel = field.get('label');
    });
    this.fieldId = id;
    this.deleteFieldDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let field = this.store.peekRecord('school-options/field', this.fieldId);

        // Delete record (locally + pushed to server).
        return field
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            field.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteFieldDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // Field Options
  @action
  addOption() {
    if (isEmpty(this.get('field.options'))) {
      this.field.set('options', []);
    }
    // field.options is a @fragmentArray, not a plain/native array — it's a
    // FragmentArray (EmberObject + MutableArray, not `extends Array`), so it
    // has no native `.push` at all. `pushObject` routes through `replace()`,
    // which is where ember-data-model-fragments marks the fragment dirty and
    // notifies observers — native mutation would both throw (no such
    // method) and skip that dirty-tracking/reactivity.
    if (this.selectDataType.value === DataType.INTEGER) {
      this.field.options.pushObject({
        valueInt: parseInt(this.get('newOption.value'), 10),
        label: this.get('newOption.label'),
      });
    } else {
      this.field.options.pushObject({
        value: this.get('newOption.value'),
        label: this.get('newOption.label'),
      });
    }
    this.newOption = { value: '', label: '' };
  }

  @action
  removeOption(option) {
    this.field.options.removeObject(option);
  }

  @action
  maybeMutBoth(a, b, context, newValue) {
    if (get(context, a) === get(context, b)) {
      set(context, b, newValue);
    }
    set(context, a, newValue);
  }

  // Option Sets
  @action
  async removeSet(item) {
    let optionSetIds = await this.field.optionSetIds;
    let index = optionSetIds.indexOf(item);
    if (index !== -1) {
      optionSetIds.splice(index, 1);
    }
  }

  @action
  async addSet(item) {
    (await this.field.optionSetIds).push(item);
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceIndex, targetIndex }) {
    if (sourceIndex === targetIndex) {
      return;
    }

    let fields = [...this.sortedFields];
    let [item] = fields.splice(sourceIndex, 1);
    fields.splice(targetIndex, 0, item);

    fields.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  @action
  optionSortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // options is a @fragmentArray; .splice() comes from the
    // fragment-array-mutable initializer (StatefulArray only has
    // .replace() natively).
    const [item] = sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  init() {
    super.init(...arguments);
    this.resetForm();
  }

  getInputType(inputType) {
    let types = this.InputType.values;
    return findBy(types, 'value', inputType);
  }

  getNumericType(dataType) {
    let types = this.numericTypes;
    return findBy(types, 'value', dataType);
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    let field = this.field;
    if (field && field.get('currentState.isDirty')) {
      field.rollbackAttributes();
    }
    this.field = this.store.createRecord('school-options/field', {});
    this.fieldId = '';
    this.fieldType = { value: 1, label: 'Text' };
    this.newOption = { value: '', label: '' };
    this.set('selectDataType', this.selectDataTypes[0]);
  }
}
